/**
 * Zwei Regeln auf DERSELBEN Google-Places-Antwort.
 *
 *  1. **Trefferauswahl bei Mehrdeutigkeit** (R3.3, Konzept § 4.7, Befund B6): Was auf dem Schirm
 *     steht, wenn Google mehrere Betriebe geliefert hat und noch niemand entschieden hat, welcher
 *     gemeint ist.
 *  2. **Warnsignal „dauerhaft geschlossen"** (R4.2, § 4.7): Was am Entscheidungsort steht, wenn
 *     Google `business_status = 'CLOSED_PERMANENTLY'` meldet.
 *
 * Beide werten dieselbe Antwort aus, beide gehören deshalb in dieselbe Datei — und beide sind
 * ANZEIGE-Regeln, keine Sperren: sie beschreiben, was eine Quelle sagt, und überlassen die
 * Entscheidung dem Betreiber.
 *
 * ── Warum ein eigenes Paket und kein `if` in der Komponente ───────────────────────────────────
 * `apps/web` hat KEINEN Test-Runner (Befund aus R0.2, seither in R0.7/R0.9/R1.6/R4.5 bestätigt).
 * Sätze wie „bei Mehrdeutigkeit steht KEIN Vorschlag da" oder „ein offener Betrieb erzeugt kein
 * Signal" sind in einer React-Komponente unprüfbar — genau dort verschwinden sie leise wieder.
 * Dieselbe Bauform wie `@maestro/anreicherung` (R0.7), `@maestro/firmenprofil` (R0.9),
 * `@maestro/recherche-stand` (R1.6), `@maestro/google-attribution` (R4.5): reine Regel im Paket,
 * Tests in `apps/api/test/places-mehrdeutig.test.ts` (jene Suite läuft garantiert in der CI).
 * Dieses Paket hat deshalb bewusst KEIN `test`-Skript — `apps/api/test/ci-testabdeckung.test.ts`
 * verlangt für jedes Paket MIT `test`-Skript einen eigenen CI-Aufruf.
 *
 * ── Warum NICHT `@maestro/anreicherung` ──────────────────────────────────────────────────────
 * Jenes Paket beantwortet EINE Frage: „In welchem Zustand ist die Recherche, welcher eine Satz
 * gehört dazu, welche eine Handlung gibt es?" — und R0.7 prüft die Vollständigkeit dieser Liste
 * gegen eine abgenommene Anzahl. Der Zustand `mehrdeutig` und sein Satz („N mögliche Treffer —
 * bitte auswählen.") stehen dort und bleiben dort; hier steht, WIE die Auswahl aussieht. Die
 * Zustandsliste wird durch dieses Paket nicht angefasst.
 *
 * Der Zustand `mehrdeutig` trägt dort weiterhin KEINE einzelne Handlung, und das ist kein
 * Versehen: an dieser Stelle steht keine Schaltfläche, sondern eine LISTE — und eine Auswahl aus
 * N Einträgen passt nicht in ein Feld „eine Handlung". Was sich mit Migration 245 geändert hat:
 * das Handelsregister hat jetzt dieselbe Auswahl (`registerTrefferauswahl()` in
 * `@maestro/firmenprofil`), und beide liefern die gemeinsame Form `Trefferliste` aus
 * `@maestro/anreicherung`. Die frühere Schieflage („nur Places kann das") ist damit weg.
 *
 * ── Reinheit ─────────────────────────────────────────────────────────────────────────────────
 * Kein `new Date()` ohne Argument, keine implizite Zeitzone: `jetzt` und `zeitzone` sind
 * Parameter. Ohne `zeitzone` formatiert `Intl` in der Zone der Laufzeit — im Browser also in der
 * des Betrachters (Vorgabe aus R0.7), im Test in der übergebenen Zone (deterministisch).
 */

import { trefferauswahlSatz, type TrefferEintrag, type Trefferliste } from "@maestro/anreicherung";
import { standZeile } from "@maestro/recherche-stand";

/* ────────────────────────────── Gemeinsame Eingabe ─────────────────────────────────────────── */

/**
 * Ein Treffer, so wie er in `google_places_lookups.candidates` steht (Migration 234) und wie die
 * API ihn ausliefert.
 */
export interface PlacesTreffer {
  placeId: string;
  name?: string | null;
  formattedAddress?: string | null;
  phone?: string | null;
  rating?: number | null;
  userRatingsTotal?: number | null;
  openingHours?: { weekdayDescriptions: string[] } | null;
  businessStatus?: string | null;
}

/** Die Quelle, unter der beide Regeln ihre Herkunftszeile führen (R1.6). Genau ein Name. */
const QUELLE = "Google Places" as const;

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * 1) R3.3 — die Trefferauswahl
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

export interface TrefferauswahlLage {
  /** Lebenszyklus-Status der Zeile (Migration 221). Nur `'ambiguous'` öffnet eine Auswahl. */
  status?: string | null;
  /** Die gespeicherten Treffer — `null`/leer heißt: es steht keine Auswahl offen. */
  treffer?: readonly PlacesTreffer[] | null;
  /** Wann Google gefragt wurde. Der Stand der ganzen Liste — sie stammt aus EINEM Abruf. */
  lookedUpAt?: string | null;
  /** IANA-Zone. Fehlt sie, gilt die Zone der Laufzeit (= des Betrachters). */
  zeitzone?: string;
}

/**
 * Ein Eintrag der Google-Trefferauswahl.
 *
 * Die Form ist seit Migration 245 die quellenneutrale aus `@maestro/anreicherung`, damit die
 * Register-Auswahl (`registerTrefferauswahl()` in `@maestro/firmenprofil`) und diese hier an
 * derselben Fläche gerendert werden können — eine Entscheidung, eine Bauform. `id` trägt hier die
 * Google-`place_id`; sie heißt nicht mehr `placeId`, weil zwei Namen für denselben Wert genau die
 * Stelle sind, an der eine gemeinsame Fläche wieder auseinanderläuft. Der API-Körper heißt
 * weiterhin `placeId` — dort ist es der Fachbegriff der Quelle, hier der Griff der Oberfläche.
 *
 * `warnung` trägt den Hinweis aus R4.2, falls GENAU DIESER Treffer dauerhaft geschlossen ist. Er
 * steht schon in der Auswahl, nicht erst danach: „dauerhaft geschlossen" ist die nützlichste
 * Angabe überhaupt, wenn man zwischen drei gleichnamigen Betrieben wählt.
 */
export type TrefferauswahlEintrag = TrefferEintrag;

export interface Trefferauswahl extends Trefferliste {
  /**
   * Die Werte, die diese Fläche aus Google anzeigt — Eingabe für `googleAttribution()` (R4.5).
   * Sie kommen aus derselben Regel, die die Zeilen baut, damit Anzeige und Attributionspflicht
   * nicht auseinanderlaufen können.
   */
  readonly googleWerte: readonly unknown[];
}

/**
 * Die Auswahl — oder `null`, wenn keine offen ist.
 *
 * `null` heißt ausdrücklich: hier ist NICHTS zu entscheiden (eindeutiges Ergebnis, kein Treffer,
 * Abruf lief nicht). Die Karte zeigt dann ihren gewohnten Inhalt. Ein leerer Auswahlblock wäre
 * eine Frage ohne Antwortmöglichkeit.
 *
 * Die Regel ist bewusst streng: Sie öffnet eine Auswahl NUR, wenn der Status es sagt UND
 * mindestens zwei Treffer dastehen. Ein `status='ambiguous'` mit einem einzigen Treffer wäre ein
 * Widerspruch in den Daten — daraus eine „Auswahl" mit einem Knopf zu bauen, hieße den
 * Widerspruch zu verstecken.
 */
export function trefferauswahl(lage: TrefferauswahlLage): Trefferauswahl | null {
  if (lage.status !== "ambiguous") return null;
  const treffer = (lage.treffer ?? []).filter((t): t is PlacesTreffer => Boolean(t?.placeId));
  if (treffer.length < 2) return null;

  const eintraege = treffer.map<TrefferauswahlEintrag>((t) => {
    const zeilen = [t.formattedAddress, t.phone]
      .map((z) => z?.trim())
      .filter((z): z is string => Boolean(z));
    return {
      id: t.placeId,
      // Ohne Namen steht dort nicht nichts, sondern der Sachverhalt: Google hat für diesen
      // Eintrag keinen Anzeigenamen geliefert. Ein leerer Titel sähe aus wie ein Ladefehler.
      titel: t.name?.trim() || "Ohne Namen bei Google",
      zeilen,
      warnung: istDauerhaftGeschlossen(t.businessStatus) ? GESCHLOSSEN_KURZ : null,
    };
  });

  return Object.freeze({
    // Derselbe Wortlaut wie der Zustandssatz in `@maestro/anreicherung` („N mögliche Treffer —
    // bitte auswählen."), hier um das ergänzt, was der Betreiber als Nächstes wissen muss:
    // dass bis dahin nichts passiert ist. Seit Migration 245 baut ihn `trefferauswahlSatz()` —
    // dieselbe Vorlage, die auch die Register-Auswahl benutzt, mit dem Wort dieser Quelle.
    satz: trefferauswahlSatz(eintraege.length, "Betrieb"),
    herkunft: standZeile(QUELLE, lage.lookedUpAt ?? null, lage.zeitzone),
    eintraege,
    googleWerte: eintraege.flatMap((e) => [e.titel, ...e.zeilen]),
  });
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * 2) R4.2 — „Google meldet: dauerhaft geschlossen"
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

/**
 * Der eine Wert, der das Signal auslöst. Google Places (New) kennt daneben `OPERATIONAL` und
 * `CLOSED_TEMPORARILY`.
 *
 * `CLOSED_TEMPORARILY` löst BEWUSST kein Signal aus: „vorübergehend geschlossen" ist für eine
 * Firma, die ein Catering bestellt, keine Aussage über ihren Bestand — Betriebsferien und Umbau
 * stehen dort genauso wie eine Schließung. Ein Signal, das bei jedem zweiten Betrieb im August
 * angeht, wird nach zwei Wochen überlesen, und dann wirkt auch das echte nicht mehr.
 */
export const BUSINESS_STATUS_DAUERHAFT_GESCHLOSSEN = "CLOSED_PERMANENTLY";

/** Die Kurzform, die in einer Trefferzeile Platz hat. */
const GESCHLOSSEN_KURZ = "Google meldet: dauerhaft geschlossen";

export interface BetriebsstatusLage {
  /** `google_places_lookups.business_status`. */
  businessStatus?: string | null;
  /** Wann Google gefragt wurde — der Stand dieser Angabe (R1.6). */
  lookedUpAt?: string | null;
  /** Firmenname für den Satz. Fehlt er, spricht der Satz vom „Betrieb". */
  firma?: string | null;
  /** IANA-Zone. Fehlt sie, gilt die Zone der Laufzeit (= des Betrachters). */
  zeitzone?: string;
}

export interface BetriebsstatusWarnung {
  /**
   * Was dasteht. BESCHREIBEND, mit genannter Quelle — nie ein Urteil: „Google meldet …", nicht
   * „Der Betrieb ist geschlossen". Google kann falsch liegen (ein Eintrag wird von Dritten
   * gemeldet), und die Entscheidung über das Angebot gehört dem Betreiber.
   */
  readonly satz: string;
  /** „Google Places · Stand 06.08.2026" bzw. „… · ohne Datum" (R1.6). */
  readonly herkunft: string;
  /**
   * Der Zusatz, der aus dem Hinweis keine Sperre macht. Er steht in der Regel und nicht in der
   * Komponente, weil er die eigentliche Aussage trägt: das Angebot bleibt möglich.
   */
  readonly zusatz: string;
  /** Eingabe für `googleAttribution()` (R4.5) — dieselben Werte, die den Satz erzeugt haben. */
  readonly googleWerte: readonly unknown[];
}

function istDauerhaftGeschlossen(status: string | null | undefined): boolean {
  // Kein `toUpperCase()`, kein `includes`: Google liefert einen Aufzählungswert, keinen Freitext.
  // Wer hier großzügig vergliche, würde bei einem künftigen Wert wie 'CLOSED_PERMANENTLY_PENDING'
  // raten statt zu wissen.
  return status === BUSINESS_STATUS_DAUERHAFT_GESCHLOSSEN;
}

/**
 * Das Warnsignal — oder `null`.
 *
 * `null` bei allem außer `CLOSED_PERMANENTLY`: bei `OPERATIONAL`, bei `CLOSED_TEMPORARILY`, bei
 * fehlender Angabe, bei verfallenen Google-Daten (R2.7 räumt `business_status` mit den übrigen
 * Vorschlagsfeldern) und bei einem Wert, den diese Fassung nicht kennt. Ein „Status unbekannt"-
 * Hinweis wäre Lärm: er stünde bei fast jedem Kunden.
 */
export function betriebsstatusWarnung(lage: BetriebsstatusLage): BetriebsstatusWarnung | null {
  if (!istDauerhaftGeschlossen(lage.businessStatus)) return null;
  const firma = lage.firma?.trim();
  return Object.freeze({
    satz: firma
      ? `Google meldet: „${firma}" ist dauerhaft geschlossen.`
      : "Google meldet: dieser Betrieb ist dauerhaft geschlossen.",
    herkunft: standZeile(QUELLE, lage.lookedUpAt ?? null, lage.zeitzone),
    zusatz:
      "Das kann überholt oder falsch sein — Google-Einträge werden auch von Dritten gemeldet. " +
      "Das Angebot wird dadurch nicht blockiert; bitte vor dem Versand kurz prüfen.",
    googleWerte: [lage.businessStatus],
  });
}
